import React from 'react';
import { MobileReportAd } from './ReportAdLayout';
import ReportMoneyInput from './ReportMoneyInput';
import { HARD_LIMITS } from '../lib/validation.js';

const CONSENT_VERSION = 'v1.0';

const FIELDS = [
  {
    key: 'customer_name',
    label: '이름 또는 닉네임',
    type: 'text',
    unit: '',
    placeholder: '홍길동 또는 닉네임',
    hint: '리포트에 표시될 이름 또는 닉네임입니다.',
  },
  {
    key: 'current_age',
    label: '현재 나이',
    type: 'number',
    unit: '세',
    placeholder: '45',
    min: HARD_LIMITS.current_age.min, max: HARD_LIMITS.current_age.max, step: 1,
    hint: '현재 만 나이를 입력하세요.',
  },
  {
    key: 'retirement_start_age',
    label: '은퇴(연금 수령) 시작 나이',
    type: 'number',
    unit: '세',
    placeholder: '65',
    min: 0, max: 120, step: 1,
    hint: '연금을 수령하기 시작할 나이입니다. 국민연금 수령 개시 나이(보통 63~65세)를 기준으로 설정하세요.',
  },
  {
    key: 'target_monthly_pension',
    label: '목표 월 연금액',
    type: 'number',
    money: true,
    unit: '원 / 월',
    placeholder: '1250000',
    min: 0,
    hint: '은퇴 후 매달 실수령하고 싶은 생활비입니다. 세후 금액으로 입력하세요.',
  },
  {
    key: 'current_personal_pension',
    label: '현재 개인연금 적립액',
    type: 'number',
    money: true,
    unit: '원',
    placeholder: '30000000',
    min: 0,
    hint: '현재 가입된 개인연금(연금저축펀드·보험 등)의 총 적립금액입니다.',
  },
  {
    key: 'current_retirement_pension',
    label: '현재 퇴직연금(IRP) 적립액',
    type: 'number',
    money: true,
    unit: '원',
    placeholder: '20000000',
    min: 0,
    hint: '퇴직연금(IRP 포함) 계좌의 현재 잔액입니다.',
  },
  {
    key: 'expected_nps_monthly',
    label: '국민연금 예상 수령액',
    type: 'number',
    money: true,
    unit: '원 / 월',
    placeholder: '800000',
    min: 0,
    hint: '국민연금공단 내연금 조회(nps.or.kr) 결과의 예상 월 수령액을 입력하세요.',
  },
  {
    key: 'accumulation_target_return',
    label: '적립 기간 목표 수익률',
    type: 'number',
    unit: '%',
    placeholder: '7',
    min: HARD_LIMITS.accumulation_target_return.min,
    max: HARD_LIMITS.accumulation_target_return.max,
    step: 0.1,
    hint: '은퇴 전까지 연금 계좌에서 목표하는 연 복리 수익률입니다. ETF 장기 평균 참고 시 5~8% 수준.',
  },
  {
    key: 'withdrawal_target_yield',
    label: '인출 기간 목표 운용 수익률',
    type: 'number',
    unit: '%',
    placeholder: '4',
    min: HARD_LIMITS.withdrawal_target_yield.min,
    max: HARD_LIMITS.withdrawal_target_yield.max,
    step: 0.1,
    hint: '은퇴 후 자산을 운용할 때 기대하는 연 수익률입니다. 안정적 운용을 가정하면 3~5% 수준.',
  },
  {
    key: 'nps_source',
    label: '국민연금 예상액 출처',
    type: 'select',
    placeholder: 'official_lookup',
    hint: '국민연금공단 조회값인지, 직접 추정한 값인지 선택해 주세요.',
    options: [
      ['official_lookup', '국민연금공단 조회'],
      ['estimated', '직접 추정'],
      ['guessed', '대략 입력'],
      ['unknown', '알 수 없음'],
    ],
  },
  {
    key: 'input_confidence',
    label: '입력값 확신도',
    type: 'select',
    placeholder: 'high',
    hint: '입력한 연금/자산 정보의 정확도를 선택해 주세요.',
    options: [
      ['high', '높음'],
      ['medium', '보통'],
      ['low', '낮음'],
    ],
  },
  {
    key: 'job_type',
    label: '직업 유형',
    type: 'select',
    placeholder: 'employee',
    hint: '익명 통계에서 직업 유형별 경향을 보기 위한 선택 항목입니다.',
    options: [
      ['employee', '직장인'],
      ['self_employed', '자영업자'],
      ['freelancer', '프리랜서'],
      ['homemaker', '전업주부/주부'],
      ['retired', '은퇴자'],
      ['other', '기타'],
    ],
  },
  {
    key: 'report_purpose',
    label: '리포트 생성 목적',
    type: 'select',
    placeholder: 'retirement_planning',
    hint: '서비스 개선을 위한 익명 통계 분류에만 사용됩니다.',
    options: [
      ['retirement_planning', '은퇴 설계'],
      ['tax_saving', '세액공제/절세'],
      ['pension_account_setup', '연금계좌 개설'],
      ['portfolio_check', '포트폴리오 점검'],
      ['curiosity', '단순 확인'],
    ],
  },
];

const INITIAL_FORM = Object.fromEntries(FIELDS.map(f => [f.key, '']));
const REQUIRED_FIELDS = FIELDS.filter(f => ![
  'nps_source',
  'input_confidence',
  'job_type',
  'report_purpose',
].includes(f.key));

function validate(form) {
  const errors = {};
  const num = key => parseFloat(form[key]);
  const empty = key => form[key] === '' || form[key] === null || form[key] === undefined;

  if (!form.customer_name.trim()) {
    errors.customer_name = '이름 또는 닉네임을 입력해 주세요.';
  }

  if (empty('current_age') || isNaN(num('current_age')) || num('current_age') < HARD_LIMITS.current_age.min || num('current_age') > HARD_LIMITS.current_age.max) {
    errors.current_age = `${HARD_LIMITS.current_age.min}~${HARD_LIMITS.current_age.max} 사이의 나이를 입력해 주세요.`;
  }

  if (empty('retirement_start_age') || isNaN(num('retirement_start_age')) || num('retirement_start_age') < 0 || num('retirement_start_age') > 120) {
    errors.retirement_start_age = '유효한 은퇴 나이를 입력해 주세요.';
  } else if (!errors.current_age && num('retirement_start_age') <= num('current_age')) {
    errors.retirement_start_age = '은퇴 나이는 현재 나이보다 커야 합니다.';
  } else if (!errors.current_age && num('retirement_start_age') - num('current_age') > HARD_LIMITS.retirement_start_age.maxYearsToRetirement) {
    errors.retirement_start_age = `은퇴까지 남은 기간은 ${HARD_LIMITS.retirement_start_age.maxYearsToRetirement}년을 초과할 수 없습니다.`;
  }

  if (empty('target_monthly_pension') || isNaN(num('target_monthly_pension')) || num('target_monthly_pension') < HARD_LIMITS.target_monthly_pension.min) {
    errors.target_monthly_pension = '목표 월 연금액은 0보다 큰 값을 입력해 주세요.';
  }

  for (const key of ['current_personal_pension', 'current_retirement_pension', 'expected_nps_monthly']) {
    if (empty(key) || isNaN(num(key)) || num(key) < 0) {
      const label = FIELDS.find(f => f.key === key).label;
      errors[key] = `${label}은(는) 0 이상의 숫자를 입력해 주세요.`;
    }
  }

  if (
    empty('accumulation_target_return') ||
    isNaN(num('accumulation_target_return')) ||
    num('accumulation_target_return') < HARD_LIMITS.accumulation_target_return.min ||
    num('accumulation_target_return') > HARD_LIMITS.accumulation_target_return.max
  ) {
    errors.accumulation_target_return = `${HARD_LIMITS.accumulation_target_return.min} ~ ${HARD_LIMITS.accumulation_target_return.max} 사이의 수익률(%)을 입력해 주세요.`;
  }

  if (
    empty('withdrawal_target_yield') ||
    isNaN(num('withdrawal_target_yield')) ||
    num('withdrawal_target_yield') < HARD_LIMITS.withdrawal_target_yield.min ||
    num('withdrawal_target_yield') > HARD_LIMITS.withdrawal_target_yield.max
  ) {
    errors.withdrawal_target_yield = `${HARD_LIMITS.withdrawal_target_yield.min} ~ ${HARD_LIMITS.withdrawal_target_yield.max} 사이의 수익률(%)을 입력해 주세요.`;
  }

  return errors;
}

function toPayload(form) {
  return {
    customer_name:               form.customer_name.trim(),
    current_age:                 Math.trunc(parseFloat(form.current_age)),
    retirement_start_age:        Math.trunc(parseFloat(form.retirement_start_age)),
    target_monthly_pension:      parseFloat(form.target_monthly_pension),
    current_personal_pension:    parseFloat(form.current_personal_pension),
    current_retirement_pension:  parseFloat(form.current_retirement_pension),
    expected_nps_monthly:        parseFloat(form.expected_nps_monthly),
    accumulation_target_return:  parseFloat(form.accumulation_target_return) / 100,
    withdrawal_target_yield:     parseFloat(form.withdrawal_target_yield) / 100,
    nps_source:                  form.nps_source || 'official_lookup',
    input_confidence:            form.input_confidence || 'high',
    job_type:                    form.job_type || 'employee',
    report_purpose:              form.report_purpose || 'retirement_planning',
  };
}

const NUMERIC_FIELDS = REQUIRED_FIELDS.filter(f => f.type === 'number');

// Count how many numeric fields differ from their placeholder value.
function computeTracking(filledForm, startTime) {
  let field_changed_count = 0;
  for (const f of NUMERIC_FIELDS) {
    if (parseFloat(filledForm[f.key]) !== parseFloat(f.placeholder)) {
      field_changed_count++;
    }
  }
  const default_value_count = NUMERIC_FIELDS.length - field_changed_count;
  return {
    field_changed_count,
    default_value_count,
    all_defaults_used: field_changed_count === 0,
    time_to_submit_seconds: (Date.now() - startTime) / 1000,
  };
}

function computeSoftWarnings(filledForm) {
  const num = key => parseFloat(filledForm[key]);
  const warnings = [];
  const totalBalance = num('current_personal_pension') + num('current_retirement_pension');
  if (num('current_age') < 20 || num('current_age') > 80) warnings.push(['current_age_unusual', '현재 나이가 일반적인 분석 범위를 벗어납니다.']);
  if (num('retirement_start_age') < 55 || num('retirement_start_age') > 75) warnings.push(['pension_start_age_unusual', '은퇴 시작 나이가 일반적인 범위를 벗어납니다.']);
  if (num('target_monthly_pension') < 1000000 || num('target_monthly_pension') > 20000000) warnings.push(['target_pension_extreme', '목표 월 연금액이 매우 낮거나 높습니다.']);
  if (totalBalance > 1000000000) warnings.push(['high_total_pension_balance', '현재 연금 적립액 합계가 10억원을 초과합니다.']);
  if (num('expected_nps_monthly') > 2500000) warnings.push(['high_nps_amount', '국민연금 예상 수령액이 매우 높습니다.']);
  if (num('accumulation_target_return') > 10) warnings.push(['high_accumulation_return', '적립 기간 목표 수익률이 10%를 초과합니다.']);
  if (num('withdrawal_target_yield') > 7) warnings.push(['high_withdrawal_return', '인출 기간 목표 수익률이 7%를 초과합니다.']);
  if (num('accumulation_target_return') < num('withdrawal_target_yield')) warnings.push(['return_rate_inversion', '은퇴 후 수익률이 적립 기간 수익률보다 높습니다.']);
  if (num('target_monthly_pension') < num('expected_nps_monthly')) warnings.push(['target_below_nps', '목표 월 연금액이 국민연금 예상액보다 낮습니다.']);
  return warnings;
}

export default function CustomerForm({ onSubmit, loading }) {
  const startTimeRef = React.useRef(Date.now());
  const sessionIdRef = React.useRef(
    window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
  );
  const [consent, setConsent] = React.useState(false);
  const [form, setForm]       = React.useState(INITIAL_FORM);
  const [errors, setErrors]   = React.useState({});
  const [touched, setTouched] = React.useState({});
  const [pendingWarnings, setPendingWarnings] = React.useState(null);
  const [pendingFilledForm, setPendingFilledForm] = React.useState(null);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }

  function handleMoneyChange(name) {
    return (rawValue) => {
      setForm(prev => ({ ...prev, [name]: rawValue ?? '' }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      }
    };
  }

  function handleBlur(e) {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const fieldErrors = validate({ ...form });
    setErrors(prev => ({ ...prev, [name]: fieldErrors[name] }));
  }

  async function submitFilledForm(filledForm, warnings = []) {
    const tracking = computeTracking(filledForm, startTimeRef.current);
    await onSubmit({
      ...toPayload(filledForm),
      privacyConsent: { agreed: true, version: CONSENT_VERSION },
      tracking: {
        ...tracking,
        session_id: sessionIdRef.current,
      },
      soft_warning_overrides: warnings.map(([code]) => code),
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const filledForm = { ...form };
    for (const f of FIELDS) {
      if (filledForm[f.key] === '' || filledForm[f.key] === null || filledForm[f.key] === undefined) {
        filledForm[f.key] = f.placeholder ?? '';
      }
    }
    setForm(filledForm);

    setTouched(Object.fromEntries(FIELDS.map(f => [f.key, true])));

    const fieldErrors = validate(filledForm);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      const firstErrKey = FIELDS.find(f => fieldErrors[f.key])?.key;
      if (firstErrKey) document.getElementById(firstErrKey)?.focus();
      return;
    }

    const warnings = computeSoftWarnings(filledForm);
    if (warnings.length > 0) {
      setPendingWarnings(warnings);
      setPendingFilledForm(filledForm);
      return;
    }

    await submitFilledForm(filledForm);
  }

  function showError(key) {
    return touched[key] && errors[key];
  }

  const inputsDisabled = loading || !consent;

  return (
    <form onSubmit={handleSubmit} noValidate>

      {/* 개인정보 동의 박스 */}
      <div className="lb-alert lb-alert-info" style={{ marginBottom: '24px' }}>
        <p style={{ fontWeight: 800, marginBottom: '8px', color: 'var(--color-brown-800)' }}>
          🛡 개인정보 수집 및 이용 동의 (필수)
        </p>
        <p className="lb-text-muted lb-text-sm" style={{ marginBottom: '10px' }}>
          라떼비버 은퇴 플래닝 리포트 생성을 위해 아래 개인정보를 수집·이용합니다.
        </p>

        <ol style={{ marginBottom: '10px', paddingLeft: '18px', lineHeight: '1.7', fontSize: '13px', color: 'var(--color-text-sub)' }}>
          <li style={{ marginBottom: '6px' }}>
            <strong>수집·이용 목적</strong>
            <ul style={{ marginTop: '4px', paddingLeft: '16px' }}>
              <li>맞춤형 은퇴 플래닝 리포트 생성</li>
              <li>생성된 리포트 URL 조회 및 재열람</li>
              <li>서비스 개선 및 연금 설계 경향 분석을 위한 익명 통계 생성</li>
            </ul>
          </li>
          <li style={{ marginBottom: '6px' }}>
            <strong>수집 항목</strong>
            <ul style={{ marginTop: '4px', paddingLeft: '16px' }}>
              <li>이름 또는 닉네임, 나이, 은퇴 목표</li>
              <li>연금 적립액, 목표 수익률 등 사용자가 입력한 은퇴 설계 관련 정보</li>
            </ul>
          </li>
          <li style={{ marginBottom: '6px' }}>
            <strong>보유·이용 기간</strong>
            <ul style={{ marginTop: '4px', paddingLeft: '16px' }}>
              <li>원본 입력정보: 리포트 생성일부터 <strong>30일</strong> 또는 이용자 삭제 요청 시까지 보관 후 파기</li>
              <li>익명 집계 통계: 개인을 식별할 수 없는 형태로 별도 생성·보관</li>
            </ul>
          </li>
          <li style={{ marginBottom: '6px' }}>
            <strong>제3자 제공</strong>
            <ul style={{ marginTop: '4px', paddingLeft: '16px' }}>
              <li>제3자 제공 없음</li>
              <li>
                단, 서비스 제공을 위해 클라우드 인프라 및 리포트 생성 도구에 개인정보 처리가 위탁될 수 있으며,
                자세한 내용은{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-brown-700)', fontWeight: 700 }}>개인정보처리방침</a>
                에서 확인할 수 있습니다.
              </li>
            </ul>
          </li>
          <li>
            <strong>동의 거부권 및 불이익</strong>
            <ul style={{ marginTop: '4px', paddingLeft: '16px' }}>
              <li>개인정보 수집·이용에 대한 동의를 거부할 수 있습니다.</li>
              <li>다만 필수 항목에 동의하지 않을 경우 맞춤형 리포트 생성 서비스를 이용할 수 없습니다.</li>
            </ul>
          </li>
        </ol>

        <div className="lb-form-check">
          <input
            type="checkbox"
            id="privacy-consent"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
            disabled={loading}
          />
          <label htmlFor="privacy-consent">
            위 내용을 확인하였으며, 개인정보 수집 및 이용에 동의합니다.
          </label>
        </div>
      </div>

      {FIELDS.map(f => (
        <div className="lb-form-row" key={f.key}>
          <label className="lb-label" htmlFor={f.key}>{f.label}</label>
          <div className={f.unit ? 'lb-input-group' : ''}>
            {f.type === 'select' ? (
              <select
                id={f.key}
                name={f.key}
                className="lb-input lb-select"
                value={form[f.key]}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={inputsDisabled}
              >
                <option value="">선택</option>
                {f.options.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            ) : f.money ? (
              <ReportMoneyInput
                id={f.key}
                name={f.key}
                className="lb-input"
                style={showError(f.key) ? { borderColor: '#b91c1c', boxShadow: '0 0 0 3px rgba(185,28,28,0.1)' } : {}}
                value={form[f.key]}
                onValueChange={handleMoneyChange(f.key)}
                onBlur={handleBlur}
                placeholder={f.placeholder}
                disabled={inputsDisabled}
              />
            ) : (
              <input
                id={f.key}
                name={f.key}
                type={f.type}
                className="lb-input"
                style={showError(f.key) ? { borderColor: '#b91c1c', boxShadow: '0 0 0 3px rgba(185,28,28,0.1)' } : {}}
                value={form[f.key]}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={f.placeholder}
                step={f.step != null ? String(f.step) : undefined}
                min={f.min != null ? String(f.min) : undefined}
                max={f.max != null ? String(f.max) : undefined}
                disabled={inputsDisabled}
              />
            )}
            {f.unit && (
              <span className="lb-input-group-text">{f.unit}</span>
            )}
          </div>
          {showError(f.key)
            ? <div className="lb-text-error">{errors[f.key]}</div>
            : f.hint && <div className="lb-form-hint">{f.hint}</div>
          }
        </div>
      ))}

      <MobileReportAd className="report-form-mobile-ad" />

      {pendingWarnings && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="soft-warning-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(31, 24, 18, 0.35)',
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            padding: '16px',
          }}
        >
          <div className="lb-card lb-card-padded" style={{ maxWidth: '520px', width: '100%' }}>
            <h3 id="soft-warning-title" className="lb-title-sm" style={{ marginBottom: '10px' }}>
              입력값을 다시 확인해 주세요.
            </h3>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '14px', lineHeight: 1.7 }}>
              {pendingWarnings.map(([code, message]) => (
                <li key={code}>{message}</li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: '8px', marginTop: '18px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="lb-btn lb-btn-secondary"
                onClick={() => {
                  setPendingWarnings(null);
                  setPendingFilledForm(null);
                }}
              >
                수정하기
              </button>
              <button
                type="button"
                className="lb-btn lb-btn-primary"
                disabled={loading}
                onClick={async () => {
                  const warnings = pendingWarnings;
                  const filled = pendingFilledForm;
                  setPendingWarnings(null);
                  setPendingFilledForm(null);
                  await submitFilledForm(filled, warnings);
                }}
              >
                그대로 생성
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '28px' }}>
        <button
          type="submit"
          className="lb-btn lb-btn-primary"
          style={{ width: '100%', fontSize: '17px' }}
          disabled={inputsDisabled}
        >
          {loading
            ? <><span className="lb-spinner lb-spinner-sm" style={{ marginRight: '8px' }} />리포트 생성 중...</>
            : '📄 리포트 생성'
          }
        </button>
      </div>
    </form>
  );
}
